// src/servicios/crud.js
import { auth, db } from './firebase';
import { 
  createUserWithEmailAndPassword, 
  deleteUser,
  signOut,
  signInWithEmailAndPassword 
} from 'firebase/auth';
import { 
  doc, 
  collection, 
  writeBatch, 
  getDoc, 
  getDocs, 
  serverTimestamp, 
  setDoc,
  updateDoc, // Añadir esta línea
  deleteDoc  // Añadir esta línea
} from 'firebase/firestore';

// ========== HELPERS ==========
const manejarTimeout = (accion, ms = 5000) => {
  console.log(`[Timeout] Configurando timeout de ${ms}ms para operación: ${accion}`);
  return new Promise((_, reject) => 
    setTimeout(() => 
      reject(new Error(`Timeout: Operación de ${accion} excedió los ${ms/1000} segundos`)), 
    ms
  ));
};

const mapearErroresEliminacion = (error) => {
  console.error(`[ErrorHandler] Error detectado: ${error.code || 'N/A'} | ${error.message}`);
  const errores = {
    'auth/user-not-found': 'Usuario no existe en Authentication',
    'auth/wrong-password': 'Credenciales inválidas para suplantación',
    'auth/requires-recent-login': 'Reautenticación requerida. Vuelva a ingresar',
    'permission-denied': 'Permisos insuficientes en Firestore',
    'app/network-error': 'Error de conexión. Verifique su internet',
    'Timeout: Operación de creación excedió los 5 segundos': 'Creación demorada. Mejore su conexión',
    'Timeout: Operación de eliminación excedió los 5 segundos': 'Eliminación demorada. Intente nuevamente'
  };
  
  return errores[error.message] || `Error crítico: ${error.message}`;
};

// ========== ALTA DE USUARIO + CERTIFICADOS ==========
export const crearUsuario = async (usuarioData) => {
  console.log('[CrearUsuario] Iniciando proceso. Email:', usuarioData.email);
  let userCredential;
  const originalUser = auth.currentUser;
  console.log('[CrearUsuario] Usuario original en sesión:', originalUser?.uid || 'Ninguno');

  try {
    sessionStorage.setItem('isRestoringSession', 'true');
    console.log('[CrearUsuario] Bandera de restauración guardada en sessionStorage');

    await Promise.race([
      (async () => {
        // 1. Crear usuario en Authentication
        console.log('[CrearUsuario/1] Registrando en Auth...');
        userCredential = await createUserWithEmailAndPassword(
          auth,
          usuarioData.email,
          usuarioData.password
        );
        console.log('[CrearUsuario/1] Usuario Auth creado. UID:', userCredential.user.uid);

        // 2. Cerrar sesión del nuevo usuario
        console.log('[CrearUsuario/2] Cerrando sesión del nuevo usuario');
        await signOut(auth);
        console.log('[CrearUsuario/2] Sesión cerrada exitosamente');

        // 3. Restaurar sesión del superadmin
        if (originalUser) {
          console.log('[CrearUsuario/3] Restaurando sesión de superadmin');
          await auth.updateCurrentUser(originalUser);
          // Evitar getIdToken si no es necesario
          await new Promise(resolve => setTimeout(resolve, 100)); // Reducir delay
        }
// 4. Preparar datos para Firestore
console.log('[CrearUsuario/4] Preparando batch Firestore');
const batch = writeBatch(db);
const uid = userCredential.user.uid; 

        
        // Extraer solo IDs de contenidos
        const contenidosIds = usuarioData.contenidos.map(c => {
          console.log('[CrearUsuario/4] Procesando contenido ID:', c.contenidoId);
          return c.contenidoId;
        });

        // Crear documento principal
        const usuarioRef = doc(db, 'usuarios', uid);
        const { password, contenidos, ...datosFirestore } = usuarioData;
        
        batch.set(usuarioRef, {
          ...datosFirestore,
          contenidos: contenidosIds,
          uid: uid,
          fechaCreacion: new Date().toISOString()
        });
        console.log('[CrearUsuario/4] Documento usuario listo en Firestore');

        // 5. Crear certificados con estado
        console.log('[CrearUsuario/5] Generando certificados. Total:', usuarioData.contenidos.length);
        const certificadosRef = collection(usuarioRef, 'certificados');
        
        for (const contenido of usuarioData.contenidos) {
          console.log('[CrearUsuario/5] Procesando contenido:', contenido.contenidoId);
          const contenidoDoc = await getDoc(doc(db, 'contenidos', contenido.contenidoId));
          
          if (!contenidoDoc.exists()) {
            console.error('[CrearUsuario/5] Contenido no encontrado:', contenido.contenidoId);
            throw new Error(`Contenido ${contenido.contenidoId} no encontrado`);
          }

          const certRef = doc(certificadosRef);
          const contenidoData = contenidoDoc.data();
          
          batch.set(certRef, {
            codigoCert: `${contenidoData.código}-CERT-${Date.now()}`,
            cedula: usuarioData.cédula,
            propietario: `${usuarioData.nombre} ${usuarioData.apellido}`,
            curso: contenidoData.curso,
            institucion: "Academia Pixel",
            instructor: "Ing. Carlos Rojas",
            estado: contenido.estado,
            urlDescarga: 'https://...pdf',
            fechaCreacion: new Date().toISOString(),
            contenidoId: contenido.contenidoId
          });
          console.log('[CrearUsuario/5] Certificado agregado al batch:', certRef.id);
        }

        console.log('[CrearUsuario] Confirmando batch completo...');
        await batch.commit();
        console.log('[CrearUsuario] Batch confirmado. Usuario creado exitosamente');
        return uid;
      })(),
      manejarTimeout('creación')
    ]);

  } catch (error) {
    console.error('[CrearUsuario] Error en el proceso:', error.message);
    if (userCredential?.user) {
      console.warn('[CrearUsuario] Revertiendo creación en Auth. UID:', userCredential.user.uid);
      await deleteUser(userCredential.user).catch((e) => console.error('Error al revertir:', e.message));
      if (originalUser) {
        console.log('[CrearUsuario/3] Restaurando sesión de superadmin');
        await auth.updateCurrentUser(originalUser);
        // Forzar actualización del token
        await originalUser.getIdToken(true);
        console.log('[CrearUsuario/3] Token actualizado');
      }
    }
    throw new Error(mapearErroresEliminacion(error));
  } finally {
    sessionStorage.removeItem('isRestoringSession');
    console.log('[CrearUsuario] Proceso finalizado. Bandera de sesión removida');
  }
};

// ========== BAJA DE USUARIO ==========
export const eliminarUsuario = async (uid) => {
  console.log('[EliminarUsuario] Iniciando eliminación. UID:', uid);
  let userCredential = null;
  const originalAdmin = auth.currentUser;
  const batch = writeBatch(db);
  console.log('[EliminarUsuario] Admin original:', originalAdmin?.uid);

  try {
    sessionStorage.setItem('isRestoringSession', 'true');
    sessionStorage.setItem('originalAdminData', JSON.stringify({
      uid: originalAdmin.uid,
      email: originalAdmin.email,
      rol: 'superadmin',
      estado: 'activo'
    }));

    await Promise.race([
      (async () => {
        // 1. Obtener datos del usuario
        const usuarioRef = doc(db, 'usuarios', uid);
        const usuarioDoc = await getDoc(usuarioRef);
        
        if (!usuarioDoc.exists()) {
          throw new Error('Usuario no existe en Firestore');
        }
        
        const { email, cédula } = usuarioDoc.data();

        // 2. Suplantación y eliminación de Auth
        userCredential = await signInWithEmailAndPassword(auth, email, cédula);
        await deleteUser(auth.currentUser);

        // 3. Restaurar sesión admin
        await auth.updateCurrentUser(originalAdmin);
        await originalAdmin.getIdToken(true);

        // 4. Eliminar datos de Firestore (usuarios + verificador)
        const certificadosRef = collection(db, 'usuarios', uid, 'certificados');
        const verificadorRef = doc(db, 'verificador', cédula);
        
        // Eliminar certificados del usuario
        const certificadosSnapshot = await getDocs(certificadosRef);
        certificadosSnapshot.forEach(doc => batch.delete(doc.ref));
        
        // Eliminar verificaciones y documento principal
        const verificacionesRef = collection(verificadorRef, 'verificaciones');
        const verificacionesSnapshot = await getDocs(verificacionesRef);
        
        // Eliminar todas las verificaciones
        verificacionesSnapshot.forEach(doc => batch.delete(doc.ref));
        
        // Eliminar documentos principales
        batch.delete(usuarioRef);
        batch.delete(verificadorRef);

        await batch.commit();
        console.log('[EliminarUsuario] Todos los datos eliminados');

        return true;
      })(),
      manejarTimeout('eliminación')
    ]);

  } catch (error) {
    console.error('[EliminarUsuario] Error crítico:', error.message);
    if (userCredential?.user) {
      console.warn('[EliminarUsuario] Limpiando usuario temporal:', userCredential.user.uid);
      await deleteUser(userCredential.user).catch((e) => console.error('Error al limpiar:', e.message));
    }

    if (originalAdmin && auth.currentUser?.uid !== originalAdmin.uid) {
      console.log('[EliminarUsuario] Restaurando sesión original');
      await auth.updateCurrentUser(originalAdmin);
      await originalAdmin.getIdToken(true);
    }

    throw new Error(mapearErroresEliminacion(error));
  } finally {
    sessionStorage.removeItem('isRestoringSession');
    sessionStorage.removeItem('originalAdminData');
    console.log('[EliminarUsuario] SessionStorage limpiado');
    //  Remover batch.commit()
  }
};

// ========== EDICIÓN DE USUARIO ==========
// En crud.js
export const editarUsuario = async (uid, nuevosDatos) => {
  console.log('[EditarUsuario] Iniciando edición. UID:', uid, 'Datos:', nuevosDatos);
  const batch = writeBatch(db);

  try {
    const usuarioRef = doc(db, 'usuarios', uid);
    
    // 1. Obtener datos actuales
    const usuarioDoc = await getDoc(usuarioRef);
    const contenidosActuales = usuarioDoc.data().contenidos || [];
    const nuevosContenidos = nuevosDatos.contenidos;

    // 2. Calcular diferencias
    const contenidosAEliminar = contenidosActuales.filter(
      id => !nuevosContenidos.some(c => c.contenidoId === id)
    );

    const contenidosAAñadir = nuevosContenidos.filter(
      c => !contenidosActuales.includes(c.contenidoId)
    );

    // 3. Actualizar documento principal
    batch.update(usuarioRef, {
      ...nuevosDatos,
      contenidos: nuevosContenidos.map(c => c.contenidoId),
      fechaActualizacion: new Date().toISOString()
    });

    // 4. Manejar certificados existentes
    const certificadosRef = collection(db, 'usuarios', uid, 'certificados');
    const certificadosSnapshot = await getDocs(certificadosRef);
    
    certificadosSnapshot.forEach(certDoc => {
      const certData = certDoc.data();
      const nuevoEstado = nuevosContenidos.find(
        c => c.contenidoId === certData.contenidoId
      )?.estado;

      if (nuevoEstado) {
        batch.update(certDoc.ref, { estado: nuevoEstado });
      }
      
      // Eliminar certificados de contenidos removidos
      if (contenidosAEliminar.includes(certData.contenidoId)) {
        batch.delete(certDoc.ref);
      }
    });

    // 5. Crear nuevos certificados
    for (const contenido of contenidosAAñadir) {
      const contenidoDoc = await getDoc(doc(db, 'contenidos', contenido.contenidoId));
      
      if (!contenidoDoc.exists()) {
        throw new Error(`Contenido ${contenido.contenidoId} no encontrado`);
      }

      const certRef = doc(certificadosRef);
      const contenidoData = contenidoDoc.data();
      
      batch.set(certRef, {
        codigoCert: `${contenidoData.código}-CERT-${Date.now()}`,
        cedula: nuevosDatos.cédula,
        propietario: `${nuevosDatos.nombre} ${nuevosDatos.apellido}`,
        curso: contenidoData.curso,
        institucion: "Academia Pixel",
        instructor: "Ing. Carlos Rojas",
        estado: contenido.estado,
        urlDescarga: 'https://...pdf',
        fechaCreacion: new Date().toISOString(),
        contenidoId: contenido.contenidoId
      });
    }

    await batch.commit();
    console.log('[EditarUsuario] Actualización exitosa');
    return true;

  } catch (error) {
    console.error('[EditarUsuario] Error:', error.message);
    throw new Error(`Error editando usuario: ${error.message}`);
  }
};

// ========== CRUD DE CONTENIDOS ==========
export const crearContenido = async (contenidoData) => {
  try {
    const docRef = doc(collection(db, 'contenidos'));
    
    await setDoc(docRef, {
      ...contenidoData,
      contenido: contenidoData.contenido.map(tarjeta => ({
        ...tarjeta,
        tipo: tarjeta.tipo || 'externo' // Tipo por tarjeta
      })),
      visible: contenidoData.estado === 'activo',
      fechaCreacion: serverTimestamp()
    });

    return docRef.id;
  } catch (error) {
    throw new Error(`Error creando contenido: ${error.message}`);
  }
};

export const editarContenido = async (id, contenidoData) => {
  try {
    const docRef = doc(db, 'contenidos', id);
    await updateDoc(docRef, {
      ...contenidoData,
      contenido: contenidoData.contenido.map(tarjeta => ({
        ...tarjeta,
        tipo: tarjeta.tipo || 'externo' // Tipo por tarjeta
      })),
      visible: contenidoData.estado === 'activo',
      fechaActualizacion: serverTimestamp()
    });
  } catch (error) {
    throw new Error(`Error actualizando contenido: ${error.message}`);
  }
};

// eliminarContenido se mantiene igual
export const eliminarContenido = async (id) => {
  try {
    await deleteDoc(doc(db, "contenidos", id));
  } catch (error) {
    console.error("[EliminarContenido] Error:", error.message);
    throw new Error(mapearErroresEliminacion(error));
  }
};



/*VERIFICADOR*/
export const verificarUsuario = async (datos) => {
  try {
    // Validación esencial
    if (!datos.cursosActivos?.length) {
      throw new Error('No hay cursos activos para verificar');
    }

    const docRef = doc(db, 'verificador', datos.cedula);
    const verificacionRef = doc(collection(docRef, 'verificaciones'));

    const batch = writeBatch(db);
    
    // Actualización MÍNIMA del documento principal
    batch.set(docRef, {
      nombreCompleto: datos.nombreCompleto,
      ultimaVerificacion: serverTimestamp() // Solo timestamp
    }, { merge: true });

    // Registro COMPLETO en subcolección
    batch.set(verificacionRef, {
      ...datos,
      cursosActivos: datos.cursosActivos, // <- Cursos solo aquí
      fechaCreacion: serverTimestamp()
    });

    await batch.commit();
    return verificacionRef.id;

  } catch (error) {
    throw new Error(`Error en verificación: ${error.message}`);
  }
};